import { CSSProperties, MouseEvent, useState } from "react";
import { Box, Button, Icon } from "react-bulma-components";
import { useTranslation } from "react-i18next";
import {
  sessionRoute,
  templateDetailRoute,
  trySessionRoute,
  useRouteContext,
} from "../../features/route/RouteProvider";
import { colorToClassName } from "../../models/category.types";
import { applyTemplateToSession, BoardTemplate, createBoardFromTemplate } from "../../models/template";

type TemplateBoxProps = {
  userUid: string | undefined;
  template: BoardTemplate;
  sourceSession?: { boardUid: string; sessionUid: string };
  /** When true, don't render the info pill at all — used on the detail page
   * where clicking it would be a no-op (user is already on that template's
   * info page). The card wrapper still carries view-transition-name, so the
   * shared-element morph runs; only the pill's DOM presence differs. */
  hideInfoPill?: boolean;
}

export const TemplateBox = ({ userUid, template, sourceSession, hideInfoPill }: TemplateBoxProps) => {
  const { t } = useTranslation();
  const [, setRoute] = useRouteContext();
  const [isCreating, setIsCreating] = useState(false);

  const handleUseTemplate = async () => {
    // Anon path is handled by the disabled state below — the click handler is
    // only wired when userUid is present, so this guard is defensive.
    if (!userUid) return;
    setIsCreating(true);
    try {
      if (sourceSession) {
        await applyTemplateToSession(sourceSession.boardUid, sourceSession.sessionUid, template);
        setRoute(sessionRoute(sourceSession.boardUid, sourceSession.sessionUid));
      } else {
        const result = await createBoardFromTemplate(userUid, template);
        if (result) {
          setRoute(sessionRoute(result.boardUid, result.sessionUid));
        }
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleInfoPillClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    // Passing "templates" as the origin so the detail page's back link
    // routes back to /templates (rather than the default /).
    setRoute(templateDetailRoute(template.id, "templates"));
  };

  // Inline view-transition-name so the browser can interpolate this card's
  // position and size across /templates ↔ /templates/:templateId navigation.
  // Cast to CSSProperties: viewTransitionName landed in React 19's DOM types
  // but this project may predate that.
  const cardStyle: CSSProperties = {
    viewTransitionName: `template-card-${template.id}`,
  } as CSSProperties;

  return (
    <div className="board-card" style={cardStyle}>
      <Box>
        {!hideInfoPill && (
          <div className="is-pulled-right">
            <button
              type="button"
              className="button is-ghost"
              onClick={handleInfoPillClick}
              aria-label={`Learn about the ${template.title} retrospective format`}
              title={`Learn about the ${template.title} retrospective format`}
            >
              <Icon color="info" size="medium">
                <i className="icon-info is-large" aria-hidden="true"></i>
              </Icon>
            </button>
          </div>
        )}
        {hideInfoPill
          ? <h1 className="title is-4">{template.title}</h1>
          : <p className="title is-4">{template.title}</p>}
        <p>{template.description}</p>
        <br />
        <div className="tags">
          {template.columns.map((col) => (
            <span key={col.title} className={`tag is-medium ${colorToClassName(col.color)}`}>
              {col.title}
            </span>
          ))}
        </div>
        <br />
        {userUid ? (
          <Button
            color="dark"
            fullwidth
            loading={isCreating}
            onClick={handleUseTemplate}
          >
            <Icon>
              <i className={sourceSession ? "icon-th-list" : "icon-plus"} aria-hidden="true"></i>
            </Icon>
            <span>{t(sourceSession ? "templates.apply" : "templates.use")}</span>
          </Button>
        ) : (
          // Signed-out visitors: drop them straight into the URL-encoded
          // ephemeral experience. No signup, no persistence, all state lives
          // in the URL. sourceSession is auth-only (applies template to an
          // existing session), so signed-out users only ever see the try-it CTA.
          <Button
            color="dark"
            fullwidth
            onClick={() => setRoute(trySessionRoute(template.id))}
          >
            <Icon>
              <i className="icon-play" aria-hidden="true"></i>
            </Icon>
            <span>{t("templates.tryItNow")}</span>
          </Button>
        )}
      </Box>
    </div>
  )
}
